<template>
  <div class="table-container">
    <table class="table hide-medium" border="0" cellspacing="0" cellpadding="0">
      <thead class="table-header">
        <tr>
          <th v-for="data in headers" :key="data" class="table-header-item">{{ data }}</th>
        </tr>
      </thead>

      <tbody class="table-content">
        <tr v-for="company in tableValues" :key="company.id" class="table-line" @click="goTo(company.siren)">
          <td v-if="isExecutive" class="table-item name">
            <div v-if="company.firstName || company.lastName" class="item">
              <span :title="`${company.firstName} ${company.lastName}`">{{ company.firstName || '' }} {{ company.lastName || '' }}</span>
            </div>
          </td>

          <td class="table-item name">
            <div class="item">
              <span class="nomargin">{{ company.denomination || 'NA' }}</span>
            </div>
            <div v-if="company.enseigne" class="item">
              {{ $t('brand') }} : <span>{{ company.enseigne || '' }}</span>
            </div>
            <div v-if="company.businessName" class="item">
              {{ $t('commercialName') }} : <span>{{ company.businessName || '' }}</span>
            </div>
          </td>

          <td class="table-item address">
            <div v-if="!(company && company.address && company.address.lines && company.address.lines[0])" class="item">NA</div>
            <div v-for="line in company.address.lines" :key="line" class="item">{{ line }}</div>
            <div class="item">{{ company.address.zipCode }} {{ company.address.city }}</div>
          </td>

          <td class="table-item immatriculation">
            <div class="item">
              <span>{{ $t('SIREN') }} : </span> {{ $utils.sirenFormat(company?.siren) }}
            </div>
            <div v-if="!company.isUnregistered" class="item">
              <span>{{ $t('NIC') }} : </span> {{ company.nic || 'NA' }}
            </div>
            <div v-if="company.isRcs" class="item">
              <span>{{ $t('RCS') }} : </span>{{ company.rcs || 'NA' }}
            </div>
            <!-- <div class="item"> <span>{{$t('fileNumber')}} : </span>{{company.numeroDossier || 'NA'}} </div> -->
            <div v-if="company.radiationDate" class="item radie">{{ $t('unregisteredAt') }} {{ $utils.formatDate(company.radiationDate) || 'NA' }}</div>
            <div v-if="!company.isRcs" class="item no-rcs">{{ $t('isNotRcs') }}</div>
          </td>

          <td v-if="!isExecutive" class="table-item activity">
            <div class="item inline">
              <div>{{ company.activity || $t('unspecifiedActivity') }}</div>
              <a class="link-button">{{ $t('seeCompany') }}</a>
            </div>
          </td>
        </tr>
        <tr v-if="!tableValues.length" class="table-line empty">
          <td class="table-item" colspan="5">
            <div class="item" :class="{ error: errorInternal.state }">
              <div v-if="errorInternal.state" class="icon-error" />
              <div v-else-if="loading" class="lds-ring-grey">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              {{
                errorInternal.state
                  ? errorInternal.errorMessage || $t('anErrorOccured')
                  : loading
                    ? $t('searchInProgress')
                    : isExecutive
                      ? $t('noExecutives')
                      : $t('noCompanies')
              }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="table-container show-medium">
      <div v-for="company in tableValues" :key="company.id" class="table-line" @click="$router.push(`search/${company.siren}`)">
        <div class="line">
          <div class="line-content">
            <div class="item bold">{{ company.denomination || 'NA' }}</div>
          </div>
        </div>

        <div class="line">
          <div class="line-content">
            <div class="item title">{{ $t('headOffice') }}</div>
          </div>
          <div class="line-content">
            <div class="item">
              {{ company && company.address && company.address.lines ? company.address.lines.reduce((a, v) => `${a} ${v}`, '') : '' }},
              {{ company.address.zipCode }}, {{ company.address.city }}
            </div>
          </div>
        </div>

        <div class="line">
          <div class="line-content">
            <div class="item title">{{ $t('registration') }}</div>
          </div>
          <div class="line-content">
            <div class="item">SIREN : {{ company.siren || 'NA' }}</div>
          </div>
          <div class="line-content">
            <div class="item">NIC : {{ company.nic || 'NA' }}</div>
          </div>
          <div class="line-content">
            <div class="item">R.C.S : {{ company.rcs || 'NA' }}</div>
          </div>
          <div v-if="!company.isRcs" class="line-content">
            <div class="item no-rcs">{{ $t('isNotRcs') }}</div>
          </div>
          <div v-if="company.radiationDate" class="line-content">
            <div class="item radie">{{ $t('unregisteredAt') }} {{ $utils.formatDate(company.radiationDate) || 'NA' }}</div>
          </div>
        </div>

        <div class="line">
          <div class="line-content">
            <div class="item title">{{ $t('activity') }}</div>
          </div>
          <div class="line-content">
            <div class="item">{{ company.activity || 'NA' }}</div>
          </div>
        </div>

        <div class="line">
          <div class="line-content activity">
            <div class="item inline">
              <a class="link-button">{{ $t('seeCompany') }}</a>
            </div>
            <div v-if="company.isUnregistered" class="item">{{ company.unregistrationDate || 'NA' }}</div>
          </div>
        </div>
      </div>

      <div v-if="!tableValues.length" class="line error">
        <div class="line-content">
          <div class="item" :class="{ error: errorInternal.state }">
            <div v-if="errorInternal.state" class="icon-error" />
            <div v-else-if="loading" class="lds-ring">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
            {{ errorInternal.state ? errorInternal.errorMessage || $t('anErrorOccured') : loading ? $t('searchInProgress') : $t('noCompanies') }}
          </div>
        </div>
      </div>
    </div>

    <Pagination v-if="values && values.length" :pagination="pagination" :set-page="setPage" :tableValues="values" :pages="pages" :disable-dots="disableDots" />
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';
import tableMixin from '@/components/tables/TableMixin';

export default {
  components: { Pagination },
  mixins: [tableMixin],

  props: {
    error: {
      type: Object,
      required: true,
    },

    headers: {
      type: Array,
      required: true,
    },

    searchedText: {
      type: String,
      required: true,
    },

    isExecutive: {
      type: Boolean,
      required: true,
    },

    setExecutive: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      values: [],
      tableMixinConfig: {
        backendPaginated: true,
        onChange: this.changePage,
        getData: this.getData,
        maxElement: 10,
      },
      loaded: false,
      loading: true,
      totalCompanies: 0,
      totalCompaniesRcs: 0,
      totalCompaniesNoRcs: 0,
      totalRadieCompanies: 0,
      totalExecutive: 0,
      errorInternal: {
        state: false,
        errorMessage: '',
      },
    };
  },

  watch: {
    isExecutive(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.resetMixin();
      }
    },
  },

  async mounted() {
    this.loaded = true;

    if (this.error && (this.error.state || this.error.errorMessage)) {
      this.errorInternal = { ...this.error };
    }
  },

  methods: {
    getSearchInfos() {
      return {
        totalCompanies: this.totalCompanies,
        totalCompaniesRcs: this.totalCompaniesRcs,
        totalCompaniesNoRcs: this.totalCompaniesNoRcs,
        totalRadieCompanies: this.totalRadieCompanies,
        totalExecutive: this.totalExecutive,
      };
    },

    setPageToZero() {
      this.setPage(0);
    },

    goTo(siren) {
      this.$router.push({ name: 'searchByIdEnterprise', params: { id: siren } });
    },

    changePage(data) {
      const isSiren = Number(this.searchedText.replace(/\s/g, ''));
      if (data.items.length === 1 && data.count === 1 && isSiren) {
        const id = data.items[0].siren;
        this.$router.push({ name: 'searchByIdEnterprise', params: { id } });
      }
    },

    async getData(pageNumber = 0, pageSize = this.tableMixinConfig.maxElement) {
      if (!this.searchedText) return setTimeout(this.setUpMixin, 200);
      let data;
      this.errorInternal.errorMessage = '';
      this.errorInternal.state = false;
      if (this.isExecutive) {
        const searched = encodeURIComponent(this.searchedText);
        try {
          data = await this.$store.dispatch('getCompaniesExecutive', {
            pageNumber,
            pageSize,
            searchedCompany: searched,
          });
          this.totalExecutive = data.executivesSearch.executiveCount;
          this.values = data.executivesSearch.executives.items;
          this.$emit('getDate');
          this.pagination.totalLength = data.count;
          this.pagination.maxPage = Math.ceil(this.pagination.totalLength / this.pagination.maxElement);
          return { data: data.executivesSearch.executives, values: data.executivesSearch.executives.items };
        } catch {
          this.errorInternal.state = true;
          this.errorInternal.errorMessage = this.$t('aDocapostErrorOccured');
        }
      } else {
        const searched = encodeURIComponent(this.searchedText);
        try {
          data = await this.$store.dispatch('getCompanies', { pageNumber, pageSize, searchedCompany: searched });
          this.totalCompanies = data.companiesSearch.companies.count;
          this.totalCompaniesRcs = data.companiesSearch.rcsCount;
          this.totalCompaniesNoRcs = data.companiesSearch.horsRcsCount;
          this.totalRadieCompanies = data.companiesSearch.radieeCount;
          this.totalExecutive = data.companiesSearch.executiveCount;
          this.pagination.totalLength = data.count;
          this.pagination.maxPage = Math.ceil(this.pagination.totalLength / this.pagination.maxElement);
          if (!data.companiesSearch.companies.count && data.companiesSearch.executiveCount) {
            this.setExecutive(true);
            const searched = encodeURIComponent(this.searchedText);
            data = await this.$store.dispatch('getCompaniesExecutive', {
              pageNumber,
              pageSize,
              searchedCompany: searched,
            });
            this.totalExecutive = data.executivesSearch.executiveCount;
            this.values = data.executivesSearch.executives.items;
            this.$emit('getDate');
            return { data: data.executivesSearch.executives, values: data.executivesSearch.executives.items };
          }
          this.$emit('getDate');
          this.values = data.companiesSearch.companies.items;
          return { data: data.companiesSearch.companies, values: data.companiesSearch.companies.items };
        } catch {
          this.errorInternal.state = true;
          this.errorInternal.errorMessage = this.$t('aDocapostErrorOccured');
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.icon-error {
  margin: 0 12px 0 0;
  position: relative;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid colors.$red-color;
  flex-shrink: 0;
  transform: scale(0.8);

  &:after {
    position: absolute;
    content: '';
    bottom: 3px;
    left: calc(50% - 1px);
    right: calc(50% - 1px);
    height: 8px;
    background: colors.$red-color;
  }

  &:before {
    position: absolute;
    content: '';
    top: 3px;
    left: calc(50% - 1px);
    right: calc(50% - 1px);
    height: 2px;
    background: colors.$red-color;
  }
}

.table .table-content .table-line {
  background: colors.$white-color;

  &:not(.empty) {
    &:hover {
      cursor: pointer;
      background: rgba(colors.$unread-table-color, 0.2);
    }
  }

  &.empty .table-item .item {
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: colors.$lightgrey-color;
    font-size: 14px;
    font-style: italic;
  }

  &:last-child .table-item {
    border: none;
  }

  .table-item {
    border-bottom: 1px solid colors.$border-color;
    background: transparent;

    .lds-ring-grey {
      margin-right: 24px;
    }

    &.immatriculation {
      min-width: 214px;
    }

    &.name {
      width: 294px;

      .item span {
        max-width: 142px;
        margin-left: 4px;

        &.nomargin {
          max-width: 264px;
          margin: 0 4px 0 0;
        }
      }
    }

    &.address {
      width: 20%;
    }

    &.activity {
      width: 30%;
    }

    .item {
      font-weight: normal;

      &.no-rcs {
        color: colors.$lightblue-color;
      }

      &.radie {
        color: colors.$red-color;
      }

      &.error {
        color: colors.$red-color;
      }

      span {
        font-weight: 500;
        margin: 0 4px 0 0;
      }

      &.inline {
        display: flex;

        .link-button {
          cursor: pointer;
        }

        div,
        a {
          width: 50%;

          &:nth-child(2n) {
            text-align: right;
            margin-left: 8px;
          }
        }
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-medium) {
  .table-container {
    width: 100%;

    .table-line {
      width: 100%;
      min-height: 192px;
      margin: 12px 0;
      display: flex;
      flex-direction: column;

      background: colors.$unread-table-color;
      padding: 20px;
      box-sizing: border-box;
      border-radius: 4px;
    }

    .line {
      display: flex;
      align-items: flex-start;
      justify-content: space-evenly;
      flex-direction: column;
      flex-grow: 1;
      margin: 12px 0;

      &.error {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        margin: 56px 0;
      }

      &.right {
        justify-content: flex-end;
      }

      .line-content {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .item {
          display: flex;
          align-items: center;

          font-size: 13px !important;
          line-height: 20px !important;
          color: colors.$black-color !important;

          &.radie {
            color: colors.$red-color !important;
          }

          &.title {
            font-weight: 500 !important;
            font-size: 14px !important;
            color: colors.$grey-color !important;
            margin-bottom: 6px;
          }

          &.bold {
            font-weight: 500 !important;
            font-size: 15px !important;
            margin-bottom: 6px;
          }

          &.error {
            display: flex;
            align-items: center;
            color: colors.$red-color !important;
          }
        }

        &.read-indication {
          align-self: flex-end;
        }

        &.label,
        &.date {
          font-weight: 500;
          color: colors.$black-color;
        }

        &.date,
        .internal-id {
          font-size: 12px;
        }

        &.label {
          font-size: 16px;
        }
      }
    }
  }
}
</style>
